export const SITE_ORIGIN = "https://behaviouroperatingsystem.com";
export const DEFAULT_SOCIAL_IMAGE = `${SITE_ORIGIN}/assets/logo-stacked.png`;

export const PAGE_META = {
  "/": {
    title: "Operational Clarity for Reliable Execution | BOS",
    description: "BOS helps leaders examine why routine work keeps returning to managers and where clearer priorities, standards, ownership, authority or leadership behaviour may help.",
  },
  "/operational-clarity": {
    title: "What Is Operational Clarity? | Behaviour Operating System",
    description: "Understand the five conditions that help people use judgement, take ownership and execute consistently when priorities compete or work becomes difficult.",
  },
  "/how-bos-works": {
    title: "How BOS Works | From Friction to Clearer Execution",
    description: "See how BOS diagnoses one recurring execution problem, clarifies the work, aligns leaders, embeds change and checks whether execution improves.",
  },
  "/friction-review": {
    title: "Operational Friction Review | Behaviour Operating System",
    description: "A focused consulting review that examines why one recurring execution problem keeps returning and what leaders should clarify or change first.",
  },
  "/operational-clarity-review": {
    title: "Free Operational Clarity Review | Behaviour Operating System",
    description: "Check one recurring area of work with a free 18-question review covering direction, standards, ownership, authority, behaviour and day-to-day friction.",
  },
  "/insights": {
    title: "Operational Clarity Insights | Behaviour Operating System",
    description: "Practical observations on Operational Clarity, leadership behaviour, accountability, management intervention and reliable execution.",
  },
  "/executive-guide": {
    title: "Operational Clarity Executive Guide | Behaviour Operating System",
    description: "Read a practical executive guide to why capable teams can produce inconsistent results and what leaders should examine before treating it as performance.",
  },
  "/about": {
    title: "About BOS and Founder Tristan Todd | Behaviour Operating System",
    description: "Learn why Tristan Todd created Behaviour Operating System and the diagnostic discipline BOS uses to examine recurring operational problems.",
  },
  "/contact": {
    title: "Contact Behaviour Operating System | Sydney, Australia",
    description: "Contact Tristan Todd at Behaviour Operating System in Sydney to discuss a recurring operational problem, management intervention or inconsistent execution.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Behaviour Operating System",
    description: "How Behaviour Operating System collects, uses, stores and protects personal information.",
  },
  "/terms-of-use": {
    title: "Terms of Use | Behaviour Operating System",
    description: "Terms governing access to and use of the Behaviour Operating System website and its published information.",
  },
  "/subscription-confirmed": {
    title: "Subscription Confirmed | The Operational Brief",
    description: "Confirmation that a subscription to The Operational Brief has been completed.",
    robots: "noindex,follow",
  },
};

export const NOT_FOUND_META = {
  title: "Page Not Found | Behaviour Operating System",
  description: "The requested page could not be found.",
  robots: "noindex,follow",
};

const organization = {
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: "Behaviour Operating System",
  alternateName: "BOS",
  url: SITE_ORIGIN,
  logo: {
    "@type": "ImageObject",
    url: DEFAULT_SOCIAL_IMAGE,
  },
  founder: { "@id": `${SITE_ORIGIN}/about#tristan-todd` },
  location: {
    "@type": "Place",
    name: "Sydney, Australia",
  },
};

const website = {
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  url: `${SITE_ORIGIN}/`,
  name: "Behaviour Operating System",
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  inLanguage: "en-AU",
};

const founder = {
  "@type": "Person",
  "@id": `${SITE_ORIGIN}/about#tristan-todd`,
  name: "Tristan Todd",
  url: `${SITE_ORIGIN}/about`,
  jobTitle: "Founder",
  worksFor: { "@id": `${SITE_ORIGIN}/#organization` },
  homeLocation: { "@type": "Place", name: "Sydney, Australia" },
  sameAs: ["https://www.linkedin.com/in/tristanltodd/"],
};

export const siteSchemas = (pathname) => {
  if (pathname === "/") return [organization, website];
  if (pathname === "/about") return [founder];
  return [];
};

export const articleSchemas = (article) => {
  const canonical = `${SITE_ORIGIN}/insights/${article.slug}`;
  const image = article.banner ? `${SITE_ORIGIN}/content/insights/${article.banner}` : DEFAULT_SOCIAL_IMAGE;
  return [
    {
      "@type": "Article",
      "@id": `${canonical}#article`,
      headline: article.title,
      description: article.seoDescription,
      image: [image],
      datePublished: article.date,
      author: {
        "@type": "Person",
        name: article.author || "Tristan Todd",
        url: `${SITE_ORIGIN}/about`,
      },
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      inLanguage: "en-AU",
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_ORIGIN}/insights` },
        { "@type": "ListItem", position: 3, name: article.title, item: canonical },
      ],
    },
  ];
};
